import { z } from "zod";
import {
  CARD_CAPTURE_MAX_SECONDS,
  type CardCapture,
  type CardTransport,
} from "./contracts";

/**
 * A capture lives five minutes at most, and a buyer may take longer over the
 * form. The secure fields do not belong to a capture: their document makes
 * aliases with whichever capture's access token the host hands it at submit,
 * and the aliases are confirmed under that capture's id. So a submit near or
 * past a capture's end goes to a fresh capture, begun then for the same card,
 * and what the buyer typed stays in the fields.
 *
 * A fresh capture is a new start, never an old one extended: its aliases are
 * made after it began, so the CVC's window still starts before capture
 * (`completeCardCapture`). Aliases made under one capture are never confirmed
 * under another.
 */

/** Left on a capture for a submit to use it: the document's alias call
 *  (15 s at most) and then the confirmation. */
export const CAPTURE_RENEW_MARGIN_MS = 30_000;

/** What the buyer is told if a capture still ends before it is confirmed
 *  (a fresh one did not help): not a card failure, and how to go on. */
export const CAPTURE_EXPIRED_TEXT =
  "Your card details timed out. Reload the card fields and enter them again.";

/** A capture and when it ends on this device's clock. */
export type CaptureLease = { capture: CardCapture; endsAt: number };

/** `capture`, received at `receivedAt`. Its stated end is read on this
 *  device's clock, and never later than the longest a capture lives from
 *  its arrival: a clock running behind the server's cannot keep a capture
 *  past its end. One running ahead only renews it sooner. */
export function captureLease(
  capture: CardCapture,
  receivedAt: number = Date.now(),
): CaptureLease {
  const latest = receivedAt + CARD_CAPTURE_MAX_SECONDS * 1000;
  const stated = capture.expiresAt ? Date.parse(capture.expiresAt) : NaN;
  return {
    capture,
    endsAt: Number.isFinite(stated) ? Math.min(stated, latest) : latest,
  };
}

/** Too little is left on the capture for a submit to use it. */
export function captureRenewalDue(
  lease: CaptureLease,
  now: number = Date.now(),
) {
  return lease.endsAt - now < CAPTURE_RENEW_MARGIN_MS;
}

/** The server's answer that a capture ended before it was confirmed. Both
 *  transports carry the code as the error's message (and `code`). */
export function isCaptureExpired(error: unknown) {
  return (
    error instanceof Error &&
    (error.message === "card_capture_expired" ||
      (error as { code?: unknown }).code === "card_capture_expired")
  );
}

/** A fresh capture for the fields drawn for `drawn`: for the same card (a
 *  code-only capture exactly for a saved card) in the same vault and route,
 *  whose token the document can make aliases with. Anything else is
 *  refused: the document cannot use it. */
export async function renewCapture(
  transport: Pick<CardTransport, "beginCardCapture">,
  cardId: string | undefined,
  drawn: CardCapture,
): Promise<CaptureLease> {
  const capture = await transport.beginCardCapture(cardId);
  if (
    !capture ||
    !z.uuid().safeParse(capture.id).success ||
    capture.refresh !== !!cardId ||
    capture.vaultId !== drawn.vaultId ||
    capture.environment !== drawn.environment ||
    capture.routeId !== drawn.routeId ||
    typeof capture.accessToken !== "string" ||
    !capture.accessToken
  )
    throw new Error("card_capture_unusable");
  return captureLease(capture);
}
